<template>
  <div>
    <app-spinner v-if="!is_loaded"/>
    <template v-else>
      <app-page-container>
        <app-flex-grid
          :rows="[
            [1, 1, 1],
            ['286.67px', '286.67px', '286.67px'],
            ['286.67px', '286.67px', '286.67px'],
            [1, 1, 1],
            ['286.67px', '286.67px', '286.67px']
          ]"
          :gap-rows="30"
        >
          <template #header-1>
            <app-cells gap="10px" :indent="false" :margin-bottom="15">
              <img alt="personal" src="@/assets/img/employment-icon.svg">
              <h3 class="title">Employment information</h3>
            </app-cells>
          </template>
          <template #col-1-1>
            <app-form-group label="Department" class="form-group--no-indent" required>
              <app-select
                @input="onDepartmentChange"
                v-model="work.department"
                :options="department_options"
                :clearable="false"
                placeholder="Company"
                :error="$v.work.department.$error"
              />
              <template #error>
                <div v-if="$v.work.department.$dirty && !$v.work.department.required">Required field</div>
              </template>
            </app-form-group>
          </template>
          <template #col-1-2>
            <app-form-group label="Position" class="form-group--no-indent" required>
              <app-select
                v-model="work.position"
                :options="work.department ? work.department.positions : []"
                :clearable="false"
                placeholder="Position"
                :error="$v.work.position.$error"
                :readonly="!work.department"
              />
              <template #error>
                <div v-if="$v.work.position.$dirty && !$v.work.position.required">Required field</div>
              </template>
            </app-form-group>
          </template>
          <template #col-1-3>
            <app-form-group label="Employment type" class="form-group--no-indent" required>
              <app-select
                v-model="work.employment_type"
                :options="employment_type_options"
                :clearable="false"
                placeholder="Employment type"
                :error="$v.work.employment_type.$error"
              />
              <template #error>
                <div v-if="$v.work.employment_type.$dirty && !$v.work.employment_type.required">Required field</div>
              </template>
            </app-form-group>
          </template>
          <template #col-2-1>
            <app-form-group
              v-if="work.position.code === 'safari_driver'"
              label="Rate per day (TSh)"
              label-for="safari_driver_per_day"
              class="form-group--no-indent"
            >
              <app-fin-number
                v-model="work.safari_driver_per_day"
                id="safari_driver_per_day"
                name="safari_driver_per_day"
                placeholder="0"
              />
            </app-form-group>
          </template>
          <template #col-2-2>
            <app-form-group
              v-if="work.position.code === 'driver'"
              label="Basic KM"
              label-for="driver_basic_km"
              class="form-group--no-indent"
              required
            >
              <app-input
                v-model.number="work.driver_basic_km"
                id="driver_basic_km"
                type="number"
                name="driver_basic_km"
                placeholder="0"
                :error="$v.work.driver_basic_km.$error"
              />
              <template #error>
                <div v-if="$v.work.driver_basic_km.$dirty && !$v.work.driver_basic_km.required">Required field</div>
              </template>
            </app-form-group>
          </template>
          <template #col-2-3>
            <app-form-group
              v-if="work.position.code === 'driver'"
              label="Allowance per KM (TSh)"
              label-for="driver_per_km"
              class="form-group--no-indent"
              required
            >
              <app-input
                v-model.number="work.driver_per_km"
                type="number"
                id="driver_per_km"
                name="driver_per_km"
                placeholder="0"
                :error="$v.work.driver_per_km.$error"
              />
              <template #error>
                <div v-if="$v.work.driver_per_km.$dirty && !$v.work.driver_per_km.required">Required field</div>
              </template>
            </app-form-group>
          </template>
          <template #col-3-1>
            <app-form-group label="Employment status" class="form-group--no-indent" required>
              <app-select
                v-model="work.employment_status"
                :options="employment_status_options"
                :clearable="false"
                placeholder="Employment status"
                :error="$v.work.employment_status.$error"
              />
              <template #error>
                <div v-if="$v.work.employment_status.$dirty && !$v.work.employment_status.required">Required field
                </div>
              </template>
            </app-form-group>
          </template>
          <template #col-3-2>
            <app-form-group label="Date of employment" class="form-group--no-indent" required>
              <app-date-picker
                v-model="work.date_of_employment"
                placeholder="Specify a date"
                :error="$v.work.date_of_employment.$error"
              />
              <template #error>
                <div v-if="$v.work.date_of_employment.$dirty && !$v.work.date_of_employment.maxValue">
                  Date cannot be less than the current date
                </div>
                <div v-if="$v.work.date_of_employment.$dirty && !$v.work.date_of_employment.required">Required field
                </div>
              </template>
            </app-form-group>
          </template>
          <template #col-3-3>
            <app-form-group
              v-if="work.employment_status && work.employment_status.code !== 'working'"
              label="Date of termination"
              class="form-group--no-indent"
              required
            >
              <app-date-picker
                v-model="work.date_of_termination"
                placeholder="Specify a date"
                :error="$v.work.date_of_termination.$error"
              />
              <template #error>
                <div v-if="$v.work.date_of_termination.$dirty && !$v.work.date_of_termination.required">
                  Required field
                </div>
              </template>
            </app-form-group>
          </template>
          <template #header-4 v-if="work.employment_status && work.employment_status.code !== 'working'">
            <h3 class="title title--mb15">Termination documents</h3>
          </template>
          <template #col-4-1 v-if="work.employment_status && work.employment_status.code !== 'working'">
            <app-form-group label="Termination letter" class="form-group--no-indent">
              <app-uploader
                v-model="work.termination_letter"
                :extensions="[
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'application/vnd.ms-excel',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'application/zip',
                    'application/pdf',
                    'image/jpeg',
                    'image/png',
                    'image/tiff',
                    'image/bmp'
                  ]"
                formDataFormats="DOC,DOCX,XLS,XLSX,ZIP,PDF,JPEG,BMP,PNG,TIFF"
              />
            </app-form-group>
          </template>
          <template #col-4-2 v-if="work.employment_status && work.employment_status.code !== 'working'">
            <app-form-group label="Certificate of service" class="form-group--no-indent">
              <app-uploader
                v-model="work.certificate_of_service"
                :extensions="[
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'application/vnd.ms-excel',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'application/zip',
                    'application/pdf',
                    'image/jpeg',
                    'image/png',
                    'image/tiff',
                    'image/bmp'
                  ]"
                formDataFormats="DOC,DOCX,XLS,XLSX,ZIP,PDF,JPEG,BMP,PNG,TIFF"
              />
            </app-form-group>
          </template>
          <template #col-4-3 v-if="work.employment_status && work.employment_status.code !== 'working'">
            <app-form-group label="Letter to NSSF" class="form-group--no-indent">
              <app-uploader
                v-model="work.nssf_letter"
                :extensions="[
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'application/vnd.ms-excel',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'application/zip',
                    'application/pdf',
                    'image/jpeg',
                    'image/png',
                    'image/tiff',
                    'image/bmp'
                  ]"
                formDataFormats="DOC,DOCX,XLS,XLSX,ZIP,PDF,JPEG,BMP,PNG,TIFF"
              />
            </app-form-group>
          </template>
          <template #header-5>
            <app-cells gap="10px" :indent="false" :margin-bottom="15">
              <img alt="personal" src="@/assets/img/payment-details-icon.svg">
              <h3 class="title">Payment details</h3>
            </app-cells>
          </template>
          <template #col-5-1>
            <app-form-group class="form-group--center form-group--no-indent" label="Payment method" required>
              <app-cells :indent="false" position="start">
                <app-radio
                  v-for="item in payment_method_options"
                  :key="item.code"
                  v-model="work.payment_method"
                  :checked="work.payment_method"
                  :value="item.code"
                  name="payment_method"
                >
                  <template #label>{{ item.name }}</template>
                </app-radio>
              </app-cells>
            </app-form-group>
          </template>
          <template #col-5-2>
            <app-form-group v-if="work.payment_method === 'bank'" label="Bank" class="form-group--no-indent" required>
              <app-select
                v-model="work.bank"
                :options="bank_options"
                :clearable="false"
                :searchable="false"
                label="short_name"
                placeholder="Bank"
                :error="$v.work.bank.$error"
              />
              <template #error>
                <div v-if="$v.work.bank.$dirty && !$v.work.bank.required">Required field</div>
              </template>
            </app-form-group>
          </template>
          <template #col-5-3>
            <app-form-group
              v-if="work.payment_method === 'bank'"
              label="Account number"
              label-for="account_number"
              class="form-group--no-indent"
              required
            >
              <app-input
                v-model="work.bank_account"
                id="account_number"
                name="account_number"
                placeholder="0"
                :error="$v.work.bank_account.$error"
              />
              <template #error>
                <div v-if="$v.work.bank_account.$dirty && !$v.work.bank_account.required">Required field</div>
              </template>
            </app-form-group>
          </template>
        </app-flex-grid>
        <app-cells :indent="false" position="end">
          <app-button ref="submit_work" @click.native="onCheckWorkForm">
            Save
          </app-button>
        </app-cells>
      </app-page-container>
      <app-cells>
        <h2 class="title">Agreements of employment</h2>
        <app-button size="small" @click.native="onAddAgreementPopupOpen">Add agreement</app-button>
        <app-cells :indent="false">
          Contract renewed by CEO: <app-switcher @change="onChangeContractRenewed" v-model="employee.contract_renew_by_ceo" />
        </app-cells>
      </app-cells>
      <v-client-table
        :class="{'table-default--loading': table_preloader}"
        :columns="table.columns"
        :data="table.items"
        :options="table.options"
        class="table-default"
      >
        <template #h__legal_entity>
          <div class="table-default__left">Legal entity</div>
        </template>
        <template #h__period>
          <div class="table-default__left">Period</div>
        </template>
        <template #h__created>
          <div class="table-default__left">Created at</div>
        </template>
        <template #h__updated>
          <div class="table-default__left">Updated at</div>
        </template>
        <template #id="{ row }">#{{ row.id }}</template>
        <template #legal_entity="{ row }">
          <div class="table-default__left">{{ row.legal_entity.short_name }}</div>
        </template>
        <template #period="{ row }">
          <div class="table-default__left">{{ formatDate(row.from_date) }} - {{ formatDate(row.to_date) }}</div>
        </template>
        <template #created="{ row }">
          <div class="table-default__left">
            <div v-if="row.created_by" class="table-default__controls">
              <div class="table-default__photo">
                <img
                  v-tooltip="{ content: row.created_by.full_name, placement: 'top-start', offset: 10 }" :src="getUserAvatar(row.created_by.crop.crop_url)"
                  alt=""
                >
              </div>
              {{ formatDateTime(row.created_at) }}
            </div>
          </div>
        </template>
        <template #updated="{ row }">
          <div class="table-default__left">
            <div v-if="row.updated_by" class="table-default__controls">
              <div class="table-default__photo">
                <img
                  v-tooltip="{ content: row.updated_by.full_name, placement: 'top-start', offset: 10 }" :src="getUserAvatar(row.updated_by.crop.crop_url)"
                  alt=""
                >
              </div>
              {{ formatDateTime(row.updated_at) }}
            </div>
          </div>
        </template>
        <template #scancopy="{ row }">
          <div class="table-default__controls">
            <button v-if="row.attachment" type="button"
                    @click="onDownloadScanCopy(row.id, row.attachment, 'agreements_em')">
              <img alt="" src="@/assets/img/download-table-blue-icon.svg">
            </button>
            <button v-else disabled type="button"><img alt="" src="@/assets/img/not-check-red-icon.svg"></button>
          </div>
        </template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button type="button" @click="onShowModalEdit(row)">
              <img alt="Edit" src="@/assets/img/edit-table-icon.svg">
            </button>
          </div>
        </template>
      </v-client-table>
      <template v-if="table.items.length">
        <app-cells>
          <h2 class="title">Salary</h2>
          <app-button size="small" @click.native="onAddSalaryPopupOpen">Add salary</app-button>
        </app-cells>
        <v-client-table
          :class="{ 'table-default--loading': table_preloader_salary }"
          :columns="table_salary.columns"
          :data="table_salary.items"
          :options="table_salary.options"
          class="table-default"
        >
          <template #h__period>
            <div class="table-default__left">Period</div>
          </template>
          <template #h__created>
            <div class="table-default__left">Created at</div>
          </template>
          <template #h__updated>
            <div class="table-default__left">Updated at</div>
          </template>
          <template #contract="{ row }">#{{ row.agreement.id }}</template>
          <template #period="{ row }">
            <div class="table-default__left">{{ formatDate(row.from_date) }} - {{ formatDate(row.to_date) }}</div>
          </template>
          <template #basic="{ row }">{{ row.basic.toLocaleString() }} TSh</template>
          <template #bonus="{ row }">{{ row.allowance.toLocaleString() }} TSh</template>
          <template #addendum="{ row }">
            <div class="table-default__controls">
              <button v-if="row.attachment" type="button"
                      @click="onDownloadScanCopy(row.id, row.attachment, 'addendum')">
                <img alt="" src="@/assets/img/download-table-blue-icon.svg">
              </button>
              <button v-else disabled type="button"><img alt="" src="@/assets/img/not-check-red-icon.svg"></button>
            </div>
          </template>
          <template #created="{ row }">
            <div class="table-default__left">
              <div v-if="row.created_by" class="table-default__controls">
                <div class="table-default__photo">
                  <img
                    v-tooltip="{ content: row.created_by.full_name, placement: 'top-start', offset: 10 }" :src="getUserAvatar(row.created_by.crop.crop_url)"
                    alt=""
                  >
                </div>
                {{ formatDateTime(row.created_at) }}
              </div>
            </div>
          </template>
          <template #updated="{ row }">
            <div class="table-default__left">
              <div v-if="row.updated_by" class="table-default__controls">
                <div class="table-default__photo">
                  <img
                    v-tooltip="{ content: row.updated_by.full_name, placement: 'top-start', offset: 10 }" :src="getUserAvatar(row.updated_by.crop.crop_url)"
                    alt=""
                  >
                </div>
                {{ formatDateTime(row.updated_at) }}
              </div>
            </div>
          </template>
          <template #controls="{ row }">
            <div class="table-default__controls">
              <button type="button" @click="onShowModalSalaryEdit(row)"><img alt="Edit"
                                                                             src="@/assets/img/edit-table-icon.svg"></button>
            </div>
          </template>
        </v-client-table>
      </template>
      <app-cells><h2 class="title">Benefits</h2></app-cells>
      <v-client-table
        :columns="table_bonus.columns"
        :data="table_bonus.items"
        :options="table_bonus.options"
        class="table-default"
      >
        <template #h__reason>
          <div class="table-default__left">Reason</div>
        </template>
        <template #h__created>
          <div class="table-default__left">Created at</div>
        </template>
        <template #h__updated>
          <div class="table-default__left">Updated at</div>
        </template>
        <template #month="{ row }">{{ monthFormat(row.employee_month.month) }}</template>
        <template #date="{ row }">{{ dateFormat(row.date) }}</template>
        <template #reason="{ row }">
          <div class="table-default__left">{{ row.reason }}</div>
        </template>
        <template #amount="{ row }">{{ row.amount.toLocaleString() || '-' }} TSh</template>
        <template #nhif="{ row }">
          <span
            :style="{ 'backgroundColor': row.nhif ? '#77b017' : '#e85c20' }"
            class="status-tag"
          >
              {{ row.nhif ? 'Yes' : 'No' }}
            </span>
        </template>
        <template #scancopy="{ row }">
          <div class="table-default__controls">
            <button v-if="row.attachment" type="button" @click="onDownloadScanCopy(row.id, row.attachment, 'benefit')">
              <img alt="" src="@/assets/img/download-table-blue-icon.svg">
            </button>
            <button v-else disabled type="button"><img alt="" src="@/assets/img/not-check-red-icon.svg"></button>
          </div>
        </template>
        <template #created="{ row }">
          <div class="table-default__left">
            <div v-if="row.created_by" class="table-default__controls">
              <div class="table-default__photo">
                <img
                  v-tooltip="{ content: row.created_by.full_name, placement: 'top-start', offset: 10 }" :src="getUserAvatar(row.created_by.crop.crop_url)"
                  alt=""
                >
              </div>
              {{ formatDateTime(row.created_at) }}
            </div>
            <template v-else>SYSTEM</template>
          </div>
        </template>
        <template #updated="{ row }">
          <div class="table-default__left">
            <div v-if="row.updated_by" class="table-default__controls">
              <div class="table-default__photo">
                <img
                  v-tooltip="{ content: row.updated_by.full_name, placement: 'top-start', offset: 10 }" :src="getUserAvatar(row.updated_by.crop.crop_url)"
                  alt=""
                >
              </div>
              {{ formatDateTime(row.updated_at) }}
            </div>
            <template v-else>SYSTEM</template>
          </div>
        </template>
      </v-client-table>
      <app-cells>
        <h2 class="title">Warnings</h2>
      </app-cells>
      <v-client-table
        :columns="table_warnings.columns"
        :data="table_warnings.items"
        :options="table_warnings.options"
        class="table-default"
      >
        <template #h__reason>
          <div class="table-default__left">Reason</div>
        </template>
        <template #h__created>
          <div class="table-default__left">Created at</div>
        </template>
        <template #h__updated>
          <div class="table-default__left">Updated at</div>
        </template>
        <template #month="{ row }">{{ monthFormat(row.employee_month.month) }}</template>
        <template #date="{ row }">{{ dateFormat(row.date) }}</template>
        <template #reason="{ row }">
          <div class="table-default__left">{{ row.reason }}</div>
        </template>
        <template #penalty="{ row }">{{ row.penalty.toLocaleString() || '-' }} TSh</template>
        <template #scancopy="{ row }">
          <div class="table-default__controls">
            <button
              v-if="row.scancopy"
              type="button"
              @click="onDownloadScanCopy(row.id, row.scancopy, 'warnings')"
            >
              <img alt="" src="@/assets/img/download-table-blue-icon.svg">
            </button>
            <button v-else disabled type="button"><img alt="" src="@/assets/img/not-check-red-icon.svg"></button>
          </div>
        </template>
        <template #created="{ row }">
          <div class="table-default__left">
            <div v-if="row.created_by" class="table-default__controls">
              <div class="table-default__photo">
                <img
                  v-tooltip="{ content: row.created_by.full_name, placement: 'top-start', offset: 10 }" :src="getUserAvatar(row.created_by.crop.crop_url)"
                  alt=""
                >
              </div>
              {{ formatDateTime(row.created_at) }}
            </div>
          </div>
        </template>
        <template #updated="{ row }">
          <div class="table-default__left">
            <div v-if="row.updated_by" class="table-default__controls">
              <div class="table-default__photo">
                <img
                  v-tooltip="{ content: row.updated_by.full_name, placement: 'top-start', offset: 10 }" :src="getUserAvatar(row.updated_by.crop.crop_url)"
                  alt=""
                >
              </div>
              {{ formatDateTime(row.updated_at) }}
            </div>
          </div>
        </template>
      </v-client-table>
    </template>
    <modal
      name="agreement"
      :width="400"
      :adaptive="true"
      :clickToClose="false"
      :height="'auto'"
      :reset="true"
      :scrollable="true"
      class="modal-not-overflow"
      draggable=".popup__title"
    >
      <div class="popup">
        <button class="popup__close" type="button" @click="$modal.hide('agreement')"></button>
        <div class="popup__title">
          {{ is_agreement_add ? 'Add agreement of employment' : 'Update agreement of employment' }}
        </div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Legal entity" required>
              <app-select
                v-model="form.legal_entity"
                :options="legal_entity_options"
                :clearable="false"
                :searchable="false"
                label="short_name"
                placeholder="Legal entity"
                :error="$v.form.legal_entity.$error"
              />
              <template #error>
                <div v-if="$v.form.legal_entity.$dirty && !$v.form.legal_entity.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="From" required>
              <app-date-picker
                @input="$refs.to_date_agreement.$children[0].focus()"
                v-model="form.from_date"
                placeholder="From"
                :error="$v.form.from_date.$error"
              />
              <template #error>
                <div v-if="$v.form.from_date.$dirty && !$v.form.from_date.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="To" required>
              <app-date-picker
                v-model="form.to_date"
                placeholder="To"
                ref="to_date_agreement"
                :error="$v.form.to_date.$error"
              />
              <template #error>
                <div v-if="$v.form.to_date.$dirty && !$v.form.to_date.required">Required field</div>
                <div v-if="$v.form.to_date.$dirty && !$v.form.to_date.maxValue">
                  Date to cannot be less than the date from
                </div>
              </template>
            </app-form-group>
            <app-form-group label="Attachment" required>
              <app-uploader
                v-model="form.attachment"
                :extensions="['application/pdf']"
                class="custom-uploader--flex"
                formDataFormats="PDF"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button ref="submit" :disabled="$v.form.$error" type="submit">
                {{ is_agreement_add ? 'Add' : 'Save' }}
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="salary"
      :width="400"
      :adaptive="true"
      :clickToClose="false"
      :height="'auto'"
      :reset="true"
      :scrollable="true"
      class="modal-not-overflow"
      draggable=".popup__title"
    >
      <div class="popup">
        <button class="popup__close" type="button" @click="$modal.hide('salary')"></button>
        <div class="popup__title">
          {{ is_salary_add ? 'Add salary' : 'Update salary' }}
        </div>
        <div class="popup__body">
          <form @submit.prevent="onCheckSalaryForm">
            <app-form-group label="Agreement of employment" required>
              <app-select
                @input="onAgreementSelected"
                v-model="form_salary.agreement"
                :options="table.items"
                :clearable="false"
                :searchable="false"
                :display-field="option => `
                  #${option.id} (${dateFormat(option.from_date)}-${dateFormat(option.to_date)}) - ${option.legal_entity.short_name}
                `"
                label="id"
                placeholder="Agreement of employment"
                :error="$v.form_salary.agreement.$error"
              />
              <template #error>
                <div v-if="$v.form_salary.agreement.$dirty && !$v.form_salary.agreement.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="From" required>
              <app-date-picker
                v-model="form_salary.from_date"
                placeholder="From"
                :error="$v.form_salary.from_date.$error"
                :readonly="!form_salary.agreement"
              />
              <template #error>
                <div v-if="$v.form_salary.from_date.$dirty && !$v.form_salary.from_date.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="To" required>
              <app-date-picker
                v-model="form_salary.to_date"
                placeholder="To"
                ref="to_date_salary"
                :error="$v.form_salary.to_date.$error"
                :readonly="!form_salary.agreement"
              />
              <template #error>
                <div v-if="$v.form_salary.to_date.$dirty && !$v.form_salary.to_date.required">Required field</div>
                <div v-if="$v.form_salary.to_date.$dirty && !$v.form_salary.to_date.maxValue">
                  Date to cannot be less than the date from
                </div>
              </template>
            </app-form-group>
            <app-form-group label="Basic (TSh)" label-for="basic" required>
              <app-fin-number
                @change.native="$v.form_salary.basic.$touch()"
                v-model="form_salary.basic"
                placeholder="0"
                id="basic"
                name="basic"
                :error="$v.form_salary.basic.$error"
              />
              <template #error>
                <div v-if="$v.form_salary.basic.$dirty && !$v.form_salary.basic.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Allowance (TSh)" label-for="allowance" required>
              <app-fin-number
                @change.native="$v.form_salary.allowance.$touch()"
                v-model="form_salary.allowance"
                placeholder="0"
                id="allowance"
                name="allowance"
                :error="$v.form_salary.allowance.$error"
              />
              <template #error>
                <div v-if="$v.form_salary.allowance.$dirty && !$v.form_salary.allowance.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group
              v-if="form_salary.agreement && form_salary.agreement.salaries_count > 0"
              label="Addendum"
              required
            >
              <app-uploader
                v-model="form_salary.attachment"
                :extensions="['application/pdf']"
                class="custom-uploader--flex"
                formDataFormats="PDF"
              />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button ref="submit_salary" :disabled="$v.form_salary.$error" type="submit">
                {{ is_salary_add ? 'Add' : 'Save' }}
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getAdministrativeLegalEntities,
  getAllEmployeesContracts,
  getEmployee,
  getEmployeesBenefitsList,
  getEmployeesDepartmentList,
  getEmployeesPositionList,
  getEmployeesSalaryList,
  getEmployeesWarningList,
  getEmploymentStatuses,
  getEmploymentTypes,
  getFinanceBanks,
  getFinancePaymentMethods,
  patchAllEmployeesContract,
  patchEmloyee,
  patchEmployeesSalary,
  postAllEmployeesContract,
  postEmployeesSalary,
} from '@/http'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { format, parse, parseISO } from 'date-fns'
import axios from 'axios'

export default {
  name: 'EmployeeContractsSalaries',
  data() {
    return {
      is_loaded: false,
      is_agreement_add: false,
      is_salary_add: false,
      table_preloader: true,
      table_preloader_salary: true,
      form: {},
      form_salary: { from_date: '', to_date: '', payment_method: '' },
      work: { employment_status: null },
      position_options: [],
      payment_method_options: [],
      department_options: [],
      bank_options: [],
      employment_type_options: [],
      employment_status_options: [],
      table: {
        items: [],
        columns: ['id', 'legal_entity', 'period', 'scancopy', 'created', 'updated', 'controls'],
        options: {
          headings: {
            id: 'Contract',
            legal_entity: 'Legal entity',
            period: 'Period',
            scancopy: 'Scan copy',
            created: 'Created at',
            updated: 'Updated at',
            controls: ''
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries'
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        }
      },
      table_salary: {
        items: [],
        columns: ['contract', 'period', 'basic', 'bonus', 'addendum', 'created', 'updated', 'controls'],
        options: {
          headings: {
            contract: 'Contract',
            period: 'Period',
            basic: 'Basic',
            bonus: 'Allowance',
            addendum: 'Addendum',
            created: 'Created at',
            updated: 'Updated at',
            controls: ''
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries'
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        }
      },
      table_warnings: {
        items: [],
        columns: ['month', 'date', 'reason', 'penalty', 'scancopy', 'created', 'updated'],
        options: {
          headings: {
            month: 'Month',
            date: 'Date',
            reason: 'Reason',
            penalty: 'Penalty',
            scancopy: 'Scan copy',
            created: 'Created at',
            updated: 'Updated at'
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries'
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        }
      },
      table_bonus: {
        items: [],
        columns: ['month', 'date', 'reason', 'amount', 'nhif', 'scancopy', 'created', 'updated'],
        options: {
          headings: {
            month: 'Month',
            date: 'Date',
            reason: 'Reason',
            amount: 'Amount',
            nhif: 'NHIF',
            scancopy: 'Scan copy',
            created: 'Created at',
            updated: 'Updated at'
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries'
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        }
      },
      legal_entity_options: [],
      employee: {}
    }
  },
  validations: {
    form: {
      legal_entity: { required },
      from_date: { required },
      to_date: {
        required,
        maxValue(val) {
          return val ? new Date(val).setHours(0, 0, 0, 0) > new Date(new Date(this.form.from_date).setHours(0, 0, 0, 0)) : true
        }
      },
      attachment: { required }
    },
    form_salary: {
      agreement: { required },
      from_date: { required },
      to_date: {
        required,
        maxValue(val) {
          return val ? new Date(val).setHours(0, 0, 0, 0) > new Date(new Date(this.form_salary.from_date).setHours(0, 0, 0, 0)) : true
        }
      },
      basic: { required },
      allowance: { required },
      attachment: {
        required: requiredIf(function (data) {
          return data.agreement && data.agreement.salaries_count > 0
        })
      }
    },
    work: {
      department: { required },
      position: { required },
      employment_type: { required },
      employment_status: { required },
      date_of_employment: {
        maxValue(val) {
          return new Date(val) <= new Date()
        },
        required
      },
      date_of_termination: {
        required: requiredIf(function () {
          return this.work.employment_status && this.work.employment_status.code !== 'working'
        })
      },
      payment_method: { required },
      bank: {
        required: requiredIf(function (value) {
          return value.payment_method === 'bank'
        })
      },
      bank_account: {
        required: requiredIf(function (value) {
          return value.payment_method === 'bank'
        })
      },
      driver_basic_km: {
        required: requiredIf(function (value) {
          return value.position && value.position.code === 'driver'
        })
      },
      driver_per_km: {
        required: requiredIf(function (value) {
          return value.position && value.position.code === 'driver'
        })
      }
    },
  },
  created() {
    getEmployee(this.$route.params.id)
      .then(res => {
        this.employee = res.data
        this.work = {
          company: res.data.company,
          department: res.data.department,
          position: res.data.position,
          employment_type: res.data.employment_type,
          employment_status: res.data.employment_status,
          date_of_employment: res.data.date_of_employment,
          date_of_termination: res.data.date_of_termination,
          termination_letter: res.data.termination_letter,
          certificate_of_service: res.data.certificate_of_service,
          nssf_letter: res.data.nssf_letter,
          payment_method: res.data.payment_method,
          bank: res.data.bank,
          bank_account: res.data.bank_account,
          safari_driver_per_day: res.data.position_salary_rate_info?.safari_driver_per_day,
          driver_per_km: res.data.position_salary_rate_info?.driver_per_km,
          driver_basic_km: res.data.position_salary_rate_info?.driver_basic_km
        }
        this.is_loaded = true
      })
    this.fetchItems()
    this.fetchItemsSalary()
    getAdministrativeLegalEntities({ employment: true }).then(res => {
      this.legal_entity_options = res.data
    })
    getEmployeesPositionList().then(res => {
      this.position_options = res.data
    })
    getFinancePaymentMethods().then(res => {
      this.payment_method_options = res.data
    })
    getFinanceBanks({ salary: true }).then(res => {
      this.bank_options = res.data
    })
    getEmployeesDepartmentList().then(res => {
      this.department_options = res.data
    })
    getEmploymentTypes().then(res => {
      this.employment_type_options = res.data
    })
    getEmploymentStatuses().then(res => {
      this.employment_status_options = res.data
    })
    getEmployeesWarningList({ employee: this.$route.params.id, status: 'signed' })
      .then(res => {
        this.table_warnings.items = res.data
      })
    getEmployeesBenefitsList({ employee: this.$route.params.id, status: 'signed' })
      .then(res => {
        this.table_bonus.items = res.data
      })
  },
  methods: {
    onAgreementSelected() {
      this.form_salary.from_date = this.form_salary.agreement.from_date
      this.form_salary.to_date = this.form_salary.agreement.to_date
    },
    onDepartmentChange() {
      this.work.position = null
    },
    fetchItems() {
      this.table_preloader = true
      getAllEmployeesContracts({ employee: this.$route.params.id })
        .then(res => {
          this.table.items = res.data
          this.table_preloader = false
        })
    },
    fetchItemsSalary() {
      this.table_preloader_salary = true
      getEmployeesSalaryList(this.$route.params.id)
        .then(res => {
          this.table_salary.items = res.data
          this.table_preloader_salary = false
        })
    },
    onShowModalEdit(row) {
      this.form = { ...row }
      this.is_agreement_add = false
      this.$v.form.$reset()
      this.$modal.show('agreement')
    },
    onShowModalSalaryEdit(row) {
      this.form_salary = { ...row }
      this.is_salary_add = false
      this.$v.form_salary.$reset()
      this.$modal.show('salary')
    },
    onCheckWorkForm() {
      this.$v.work.$touch()
      if (this.$v.work.$invalid) {
        this.$notify({
          type: 'error',
          title: 'Check that the form fields are filled in correctly'
        })
      } else {
        this.sendWorkForm()
      }
    },
    sendWorkForm() {
      this.$refs.submit_work.preload = true
      patchEmloyee(this.$route.params.id, this.normalizeWork())
        .finally(() => {
          this.$refs.submit_work.preload = false
        })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Data saved successfully'
          })
        })
    },
    normalizeWork() {
      const normalizedForm = { ...this.work }

      normalizedForm.department = normalizedForm.department.code
      normalizedForm.position = normalizedForm.position.code
      normalizedForm.employment_type = normalizedForm.employment_type.code
      if (normalizedForm.employment_status) normalizedForm.employment_status = normalizedForm.employment_status.code
      if (normalizedForm.termination_letter && normalizedForm.termination_letter.id) {
        normalizedForm.termination_letter = normalizedForm.termination_letter.id
      } else normalizedForm.termination_letter = null
      if (normalizedForm.certificate_of_service && normalizedForm.certificate_of_service.id) {
        normalizedForm.certificate_of_service = normalizedForm.certificate_of_service.id
      } else normalizedForm.certificate_of_service = null
      if (normalizedForm.nssf_letter && normalizedForm.nssf_letter.id) {
        normalizedForm.nssf_letter = normalizedForm.nssf_letter.id
      } else normalizedForm.nssf_letter = null
      if (normalizedForm.payment_method === 'bank') {
        normalizedForm.bank = normalizedForm.bank.id
      } else {
        normalizedForm.bank = null
        normalizedForm.bank_account = null
      }

      return normalizedForm
    },
    onChangeContractRenewed() {
      patchEmloyee(this.$route.params.id, {contract_renew_by_ceo: this.employee.contract_renew_by_ceo})
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Data saved successfully'
          })
        })
    },
    onCheckSalaryForm() {
      this.$v.form_salary.$touch()
      if (this.$v.form_salary.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly'
        })
      } else {
        this.sendSalaryForm()
      }
    },
    sendSalaryForm() {
      this.$refs.submit_salary.preload = true
      if (this.is_salary_add) {
        postEmployeesSalary(this.$route.params.id, this.normalizeSalary())
          .finally(() => {
            this.$refs.submit_salary.preload = false
          })
          .then(() => {
            this.fetchItemsSalary()
            this.$modal.hide('salary')
            this.$notify({
              type: 'success',
              title: 'Salary added'
            })
          })
      } else {
        patchEmployeesSalary(this.$route.params.id, this.form_salary.id, this.normalizeSalary())
          .finally(() => {
            this.$refs.submit_salary.preload = false
          })
          .then(() => {
            this.fetchItemsSalary()
            this.$modal.hide('salary')
            this.$notify({
              type: 'success',
              title: 'Salary saved'
            })
          })
      }
    },
    normalizeSalary() {
      const normalizedForm = { ...this.form_salary }

      if (normalizedForm.attachment && normalizedForm.attachment.id) {
        normalizedForm.attachment = normalizedForm.attachment.id
      } else normalizedForm.attachment = null
      normalizedForm.agreement = normalizedForm.agreement.id

      return normalizedForm
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly'
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      if (this.is_agreement_add) {
        postAllEmployeesContract(this.normalizeForm())
          .finally(() => {
            this.$refs.submit.preload = false
          })
          .then(() => {
            this.fetchItems()
            this.$modal.hide('agreement')
            this.$notify({
              type: 'success',
              title: 'Agreement added'
            })
          })
      } else {
        patchAllEmployeesContract(this.form.id, this.normalizeForm())
          .finally(() => {
            this.$refs.submit.preload = false
          })
          .then(() => {
            this.fetchItems()
            this.$modal.hide('agreement')
            this.$notify({
              type: 'success',
              title: 'Agreement saved'
            })
          })
      }
    },
    normalizeForm() {
      const normalizedForm = { ...this.form }

      normalizedForm.employee = this.$route.params.id
      normalizedForm.legal_entity = normalizedForm.legal_entity.code
      if (normalizedForm.attachment && normalizedForm.attachment.id) {
        normalizedForm.attachment = normalizedForm.attachment.id
      } else normalizedForm.attachment = null

      return normalizedForm
    },
    onAddAgreementPopupOpen() {
      this.is_agreement_add = true
      this.form = {}
      this.$v.form.$reset()
      this.$modal.show('agreement')
    },
    onAddSalaryPopupOpen() {
      this.is_salary_add = true
      this.form_salary = { from_date: '', to_date: '' }
      this.$v.form_salary.$reset()
      this.$modal.show('salary')
    },
    formatDate(date) {
      if (!date) return ''
      return format(parseISO(date), 'dd MMMM yyyy')
    },
    formatDateTime(date) {
      if (!date) return ''
      return format(parseISO(date), 'dd MMMM yyyy HH:mm')
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    onDownloadScanCopy(id, doc_obj, type) {
      axios.get(doc_obj.file, { responseType: 'blob' })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          const extension = doc_obj.file_name.split('.').pop()
          let part_of_name = ''

          if (type === 'agreements') part_of_name = 'Employment agreement'
          if (type === 'benefit') part_of_name = 'Benefit'
          if (type === 'addendum') part_of_name = 'Addendum to salary'
          if (type === 'warnings') part_of_name = 'Warning'
          link.href = url
          link.setAttribute('download', `${part_of_name} ${id} - ${this.employee.full_name}.${extension}`)
          document.body.appendChild(link)
          link.click()
          URL.revokeObjectURL(url)
        })
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '-'
    },
    monthFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'MMM yyyy')
    }
  }
}
</script>
